import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LOADER_SIZE, LOADER_LABEL_VARIANTS, LOADER_VARIANTS } from '../constants';
import { ProgressTrack, LoaderLabel, ProgressIndicator } from '../elements';

const StyledDeterminateContainer = styled.div`
  max-width: 784px;
`;

const StyledProgressBarWrapper = styled.div`
  cursor: progress;
  display: flex;
  flex-direction: row;
  max-width: ${({ $trackWidth }) => `calc(${$trackWidth} + 16px)`};
`;

const StyledProgressLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ $hideLabel }) => ($hideLabel ? 'flex-end' : 'space-between')};
  max-width: ${({ $trackWidth }) => `calc(${$trackWidth} + 16px)`};
`;

const DeterminateProgressBar = ({
  className,
  dataTestId = '',
  hideLabel = false,
  hideProgress = false,
  isInline = false,
  label = 'Loading...',
  loaderSize = LOADER_SIZE.LARGE,
  percentComplete,
  trackWidth,
  ...other
}) => {
  const limitedPercentComplete = Math.min(Math.max(percentComplete, 0), 100);

  return (
    <StyledDeterminateContainer
      aria-label="Determinate Progress Bar"
      aria-valuenow={Math.round(limitedPercentComplete)}
      aria-valuemin="0"
      aria-valuemax="100"
      className={className ? `${className}-progress-bar` : undefined}
      data-testid={dataTestId ? `${dataTestId}-progress-bar` : undefined}
      role="progressbar"
    >
      {!isInline && (
        <>
          <StyledProgressLabelWrapper
            className={className ? `${className}-progress-label-wrapper` : undefined}
            data-testid={dataTestId ? `${dataTestId}-progress-label-wrapper` : undefined}
            role="status"
            $hideLabel={hideLabel}
            $trackWidth={trackWidth}
          >
            <LoaderLabel
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              {...{ className, dataTestId, hideLabel, hideProgress, isInline, label, loaderSize }}
            />
            <ProgressIndicator
              className={className ? `${className}-progress-indicator` : undefined}
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              progressLabel={limitedPercentComplete}
              {...{ dataTestId, hideLabel, hideProgress, isInline, loaderSize }}
            />
          </StyledProgressLabelWrapper>
          <StyledProgressBarWrapper
            className={className ? `${className}-progress-bar-wrapper` : undefined}
            data-testid={dataTestId ? `${dataTestId}-progress-bar-wrapper` : undefined}
            $trackWidth={trackWidth}
            {...other}
          >
            <ProgressTrack
              className={className ? `${className}-progress-track` : undefined}
              percentComplete={limitedPercentComplete}
              variant={LOADER_VARIANTS.DETERMINATE}
              {...{ dataTestId, loaderSize, trackWidth }}
            />
          </StyledProgressBarWrapper>
        </>
      )}
      {isInline && (
        <StyledProgressBarWrapper
          className={className ? `${className}-progress-bar-wrapper` : undefined}
          data-testid={dataTestId ? `${dataTestId}-progress-bar-wrapper` : undefined}
          $isInline={isInline}
          role="status"
          {...other}
        >
          <StyledProgressLabelWrapper
            className={className ? `${className}-progress-label-wrapper` : undefined}
            data-testid={dataTestId ? `${dataTestId}-progress-label-wrapper` : undefined}
          >
            <LoaderLabel
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              {...{ className, dataTestId, hideLabel, isInline, label, loaderSize }}
            >
              {label}
            </LoaderLabel>
          </StyledProgressLabelWrapper>
          <ProgressTrack
            className={className ? `${className}-progress-track` : undefined}
            percentComplete={limitedPercentComplete}
            variant={LOADER_VARIANTS.DETERMINATE}
            {...{ dataTestId, loaderSize, trackWidth }}
          />
          <ProgressIndicator
            className={className ? `${className}-progress-indicator` : undefined}
            progressLabel={limitedPercentComplete}
            labelMaxWidth={trackWidth}
            labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
            {...{ dataTestId, hideProgress, isInline, loaderSize }}
          />
        </StyledProgressBarWrapper>
      )}
    </StyledDeterminateContainer>
  );
};

DeterminateProgressBar.propTypes = {
  /** Adds additional class name to progress bar track */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If true, label will be inline with progress loader and will have a max-width of 194px. */
  isInline: PropTypes.bool,
  /** Visually hides loader text */
  hideLabel: PropTypes.bool,
  /** Visually hides percent progress text */
  hideProgress: PropTypes.bool,
  /** Adds message to left of loader */
  label: PropTypes.node,
  /** Sets the size of the loader to small or large */
  loaderSize: PropTypes.oneOf(Object.values(LOADER_SIZE)),
  /** Determines the width of the progress track fill based on percent complete */
  percentComplete: PropTypes.number.isRequired,
  /** Determines the width property of the progress track with a max-width of 768px */
  trackWidth: PropTypes.string.isRequired,
};

export { DeterminateProgressBar };
